import { ApiError } from "@/lib/api-client";

// Maps a thrown legacy-cert upload error to a single (PHI-free) card message
// (mirror of clients/client-error.ts). We branch ONLY on the structured
// `error.code` / `error.status` — never the server's `message`/`details` text — and
// always render our own copy.
//
// [Source: application/administration/upload_legacy_cert/exceptions.py — LEGACY-413
//  (too large) / LEGACY-415 (type) / LEGACY-422 (empty) / LEGACY-409 (concurrent
//  upload) / LEGACY-411 (already credited off-band); common/exceptions.py —
//  LEGACY-503 (storage unavailable); api-client NETWORK_ERROR (status 0).]

const TOO_LARGE = "That file is too large. Upload a smaller certificate file.";
const WRONG_TYPE = "That file type isn't accepted. Upload a PDF, JPG, or PNG.";
const EMPTY_FILE = "That file appears to be empty. Choose your certificate file and try again.";
const IN_PROGRESS =
  "Another upload is already in progress. Please wait a moment and try again.";
const ALREADY_VERIFIED =
  "Your AMPS certificate is already verified. No upload is needed.";
const UNAVAILABLE =
  "Certificate upload is temporarily unavailable. Please try again in a moment.";
const NETWORK = "We couldn't reach the server. Check your connection and try again.";
const GENERIC = "Something went wrong uploading your certificate. Please try again.";

/** Map an upload failure to a short, generic, PHI-free card message. */
export function toUploadCertErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.code === "LEGACY-413" || error.status === 413) return TOO_LARGE;
    if (error.code === "LEGACY-415" || error.status === 415) return WRONG_TYPE;
    if (error.code === "LEGACY-422" || error.status === 422) return EMPTY_FILE;
    // LEGACY-411 SHARES its HTTP status with the concurrent-upload race below, so it
    // must be matched on the code first — the bare `status === 409` fallback would
    // otherwise tell an already-credited clinician to wait for an upload that is not
    // happening (gh-742 / PR #743).
    if (error.code === "LEGACY-411") return ALREADY_VERIFIED;
    if (error.code === "LEGACY-409" || error.status === 409) return IN_PROGRESS;
    if (error.code === "LEGACY-503" || error.status === 503) return UNAVAILABLE;
    if (error.code === "NETWORK_ERROR" || error.status === 0) return NETWORK;
  }
  return GENERIC;
}

/** Short, generic message for a failed status load (the card's read side). */
export function toCertStatusErrorMessage(): string {
  return "We couldn't load your certificate status. Please try again.";
}
